import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

interface CurrencyMaskConfig {
  prefix?: string;
  thousands?: string;
  decimal?: string;
}

@Directive({
  selector: "input[currencyMask]",
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyMaskDirective),
      multi: true,
    },
  ],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  @Input() currencyMask: CurrencyMaskConfig | string | boolean = {};
  @Input() options: CurrencyMaskConfig = {};

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private disabled = false;

  constructor(private elementRef: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  writeValue(value: unknown): void {
    const parsed = this.parseModelValue(value);
    this.setInputValue(parsed);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.renderer.setProperty(this.elementRef.nativeElement, "disabled", isDisabled);
  }

  @HostListener("input", ["$event.target.value"])
  handleInput(rawValue: string): void {
    if (this.disabled) {
      return;
    }

    const digits = (rawValue || "").replace(/\D/g, "");

    if (!digits) {
      this.setInputValue(null);
      this.onChange(null);
      return;
    }

    const numericValue = Number(digits) / 100;
    this.setInputValue(numericValue);
    this.onChange(numericValue);
  }

  @HostListener("blur")
  handleBlur(): void {
    this.onTouched();
  }

  private setInputValue(value: number | null): void {
    this.renderer.setProperty(this.elementRef.nativeElement, "value", this.formatValue(value));
  }

  private parseModelValue(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "number" && !Number.isNaN(value)) {
      return value;
    }

    if (typeof value === "string") {
      const normalized = value.replace("R$", "").trim().replace(/\./g, "").replace(",", ".");
      const parsed = Number(normalized);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private formatValue(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return "";
    }

    const { prefix, thousands, decimal } = this.getConfig();
    const [integerPart, decimalPart] = value.toFixed(2).split(".");
    const integerWithThousands = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return `${prefix}${integerWithThousands}${decimal}${decimalPart}`;
  }

  private getConfig(): Required<CurrencyMaskConfig> {
    const inputConfig = typeof this.currencyMask === "object" && this.currencyMask !== null ? this.currencyMask : {};
    const mergedConfig = { ...inputConfig, ...this.options };

    return {
      prefix: mergedConfig.prefix ?? "R$ ",
      thousands: mergedConfig.thousands ?? ".",
      decimal: mergedConfig.decimal ?? ",",
    };
  }
}